import { Component, lazy, Suspense, type CSSProperties, type ErrorInfo, type ReactNode } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AdvertisingRevenuePopup } from './components/AdvertisingRevenuePopup'
import { NavBar } from './components/NavBar'
import { ReviewedByBenPopup } from './components/ReviewedByBenPopup'
import { SitePasswordGate } from './components/SitePasswordGate'
import { Toaster } from './components/Toaster'
import { Skeleton } from './components/ui/skeleton'
import { Button } from './components/ui/button'
import { AuthProvider, useAuth } from './lib/AuthContext'

const Home = lazy(() => import('./pages/Home').then((m) => ({ default: m.Home })))
const Browse = lazy(() => import('./pages/Browse').then((m) => ({ default: m.Browse })))
const People = lazy(() => import('./pages/People').then((m) => ({ default: m.People })))
const CreatorProfile = lazy(() => import('./pages/CreatorProfile').then((m) => ({ default: m.CreatorProfile })))
const CreatorRecord = lazy(() => import('./pages/CreatorRecord').then((m) => ({ default: m.CreatorRecord })))
const ShowPage = lazy(() => import('./pages/ShowPage').then((m) => ({ default: m.ShowPage })))
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const MyRecord = lazy(() => import('./pages/MyRecord').then((m) => ({ default: m.MyRecord })))
const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })))
const Signup = lazy(() => import('./pages/Signup').then((m) => ({ default: m.Signup })))
const Profile = lazy(() => import('./pages/Profile').then((m) => ({ default: m.Profile })))
const Reviews = lazy(() => import('./pages/Reviews').then((m) => ({ default: m.Reviews })))
const TopCreators = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.TopCreators })))
const RisingCreators = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.RisingCreators })))
const NewCreators = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.NewCreators })))
const EpisodesIndex = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.EpisodesIndex })))
const TopEpisodes = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.TopEpisodes })))
const EpisodeTopics = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.EpisodeTopics })))
const TopicPage = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.TopicPage })))
const Discover = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.Discover })))
const HiddenGems = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.HiddenGems })))
const EditorialPicks = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.EditorialPicks })))
const Charts = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.Charts })))
const HallOfFame = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.HallOfFame })))
const GuestsIndex = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.GuestsIndex })))
const GuestPage = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.GuestPage })))
const GuestMarketplace = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.GuestMarketplace })))
const CommunityLeaderboard = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.CommunityLeaderboard })))
const CommunityPolls = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.CommunityPolls })))
const CommunityContribute = lazy(() => import('./pages/MenuPages').then((m) => ({ default: m.CommunityContribute })))
const RabbitHoles = lazy(() => import('./pages/ToolPages').then((m) => ({ default: m.RabbitHoles })))
const TimestampSearch = lazy(() => import('./pages/ToolPages').then((m) => ({ default: m.TimestampSearch })))
const ColdStartQuiz = lazy(() => import('./pages/ToolPages').then((m) => ({ default: m.ColdStartQuiz })))
const ListeningRooms = lazy(() => import('./pages/ToolPages').then((m) => ({ default: m.ListeningRooms })))
const CommunityPlaylists = lazy(() => import('./pages/PlaylistPages').then((m) => ({ default: m.CommunityPlaylists })))
const PlaylistDetail = lazy(() => import('./pages/PlaylistPages').then((m) => ({ default: m.PlaylistDetail })))
const NewPlaylist = lazy(() => import('./pages/PlaylistPages').then((m) => ({ default: m.NewPlaylist })))
const PlaylistEmbed = lazy(() => import('./pages/PlaylistPages').then((m) => ({ default: m.PlaylistEmbed })))
const EditProfile = lazy(() => import('./pages/EditProfile').then((m) => ({ default: m.EditProfile })))
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })))
const JournalistProfile = lazy(() => import('./pages/JournalistProfile').then((m) => ({ default: m.JournalistProfile })))
const Knowledge = lazy(() => import('./pages/RecordedKnowledge').then((m) => ({ default: m.RecordedKnowledge })))
const KnowledgeEntryPage = lazy(() => import('./components/KnowledgeEntryPage').then((m) => ({ default: m.KnowledgeEntryPage })))
const EpisodePage = lazy(() => import('./pages/EpisodePage').then((m) => ({ default: m.EpisodePage })))
const NotificationsPage = lazy(() => import('./pages/Notifications').then((m) => ({ default: m.NotificationsPage })))
const AdminPage = lazy(() => import('./pages/Admin').then((m) => ({ default: m.AdminPage })))
const PageView = lazy(() => import('./pages/PageView').then((m) => ({ default: m.PageView })))
const PodcastDetail = lazy(() => import('./pages/PodcastDetail').then((m) => ({ default: m.PodcastDetail })))
const PodcastDiagnostics = lazy(() => import('./pages/PodcastDiagnostics').then((m) => ({ default: m.PodcastSystemDiagnostics })))
const PodcastHosts = lazy(() => import('./pages/PodcastHosts').then((m) => ({ default: m.PodcastHosts })))
const PodcastHostDetail = lazy(() => import('./pages/PodcastHostDetail').then((m) => ({ default: m.PodcastHostDetail })))
const PodcastCreators = lazy(() => import('./pages/PodcastCreators').then((m) => ({ default: m.PodcastCreators })))
const PodcastPeopleList = lazy(() => import('./pages/PodcastPeopleList').then((m) => ({ default: m.PodcastPeopleList })))
const PodcastPersonDetail = lazy(() => import('./pages/PodcastPersonDetail').then((m) => ({ default: m.PodcastPersonDetail })))
const SummariesBrowse = lazy(() => import('./pages/SummariesBrowse').then((m) => ({ default: m.SummariesBrowse })))
const SummariesEditor = lazy(() => import('./pages/SummariesEditor').then((m) => ({ default: m.SummariesEditor })))
const EpisodeSummaryPage = lazy(() => import('./pages/EpisodeSummary').then((m) => ({ default: m.EpisodeSummaryPage })))
const SummaryTopics = lazy(() => import('./pages/SummaryTopics').then((m) => ({ default: m.SummaryTopics })))
const SummaryListsPage = lazy(() => import('./pages/SummaryLists').then((m) => ({ default: m.SummaryListsPage })))
const ReadingListPage = lazy(() => import('./pages/ReadingList').then((m) => ({ default: m.ReadingListPage })))

const sceneStars: CSSProperties[] = [
  { '--size': '42px', '--left': '8%', '--top': '14%', '--opacity': '.62', '--blur': '.2px', '--appear': '8s', '--drift': '24s', '--haze': '14s', '--rotate': '8deg' } as CSSProperties,
  { '--size': '28px', '--left': '22%', '--top': '74%', '--opacity': '.46', '--blur': '.35px', '--appear': '10s', '--drift': '30s', '--haze': '16s', '--rotate': '-10deg' } as CSSProperties,
  { '--size': '54px', '--left': '38%', '--top': '26%', '--opacity': '.70', '--blur': '.18px', '--appear': '9s', '--drift': '28s', '--haze': '18s', '--rotate': '16deg' } as CSSProperties,
  { '--size': '30px', '--left': '54%', '--top': '62%', '--opacity': '.48', '--blur': '.35px', '--appear': '11s', '--drift': '34s', '--haze': '15s', '--rotate': '-14deg' } as CSSProperties,
  { '--size': '46px', '--left': '70%', '--top': '18%', '--opacity': '.66', '--blur': '.2px', '--appear': '12s', '--drift': '28s', '--haze': '17s', '--rotate': '5deg' } as CSSProperties,
  { '--size': '24px', '--left': '84%', '--top': '78%', '--opacity': '.42', '--blur': '.45px', '--appear': '8.5s', '--drift': '32s', '--haze': '13s', '--rotate': '-8deg' } as CSSProperties,
  { '--size': '36px', '--left': '90%', '--top': '38%', '--opacity': '.56', '--blur': '.28px', '--appear': '13s', '--drift': '40s', '--haze': '19s', '--rotate': '12deg' } as CSSProperties,
]

function PageFallback() {
  return (
    <main className="mx-auto max-w-[1200px] space-y-6 px-4 py-10">
      <Skeleton className="h-10 w-48" />
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-40 w-full" />
    </main>
  )
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, loading, isDemo } = useAuth()

  if (loading) {
    return (
      <main className="mx-auto max-w-[1200px] space-y-6 px-4 py-10">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-72 w-full" />
      </main>
    )
  }

  if (!session && !isDemo) return <Navigate to="/login" replace />

  return children
}

class RouteErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null; errorInfo: ErrorInfo | null }> {
  state = { hasError: false, error: null as Error | null, errorInfo: null as ErrorInfo | null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

componentDidCatch(error: Error, info: ErrorInfo) {
     console.error('[App] Route render failed', error, info)
     this.setState({ errorInfo: info })

     // Automatically recover from Vite chunk load errors caused by new deployments
     const isChunkLoadFailed =
       error?.name === 'ChunkLoadError' ||
       (error?.message || '').includes('Failed to fetch dynamically imported module') ||
       (error?.message || '').includes('Importing a module script failed') ||
       (error?.message || '').includes('Cannot read properties of undefined')

     if (isChunkLoadFailed) {
       const reloadUrl = window.location.pathname + '?v=' + Date.now()
       window.location.href = reloadUrl
     }
   }

   render() {
    if (this.state.hasError) {
      return (
        <main className="mx-auto flex min-h-[60vh] max-w-[760px] items-center px-4 py-10">
          <div className="w-full rounded-[28px] border border-[rgba(67,16,7,0.1)] bg-white p-6 text-crusta-950 shadow-[0_24px_60px_rgba(155,43,17,0.12)]">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-crusta-600">Route error</p>
            <h1 className="mt-2 text-2xl font-extrabold">This page hit a bad data edge.</h1>
            <p className="mt-3 text-sm text-crusta-800">
              The shell is still alive, but one section failed to render.
            </p>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-crusta-100 bg-crusta-50 p-3 font-mono text-xs text-crusta-800">
              <p className="font-bold">{this.state.error?.name}: {this.state.error?.message}</p>
              <pre className="mt-2 text-crusta-800">{this.state.errorInfo?.componentStack}</pre>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button type="button" onClick={() => window.location.reload()}>
                Reload page
              </Button>
              <Button asChild variant="ghost">
                <a href="/">Go home</a>
              </Button>
            </div>
          </div>
        </main>
      )
    }

    return this.props.children
  }
}

function AppShell() {
  const location = useLocation()
  const isEmbed = location.pathname.startsWith('/embed/')

  return (
    <>
      <div className="scene pointer-events-none z-0">
        <div className="stars">
          {sceneStars.map((style, index) => (
            <div key={index} className="star" style={style}></div>
          ))}
        </div>
      </div>
      <div className="relative z-10 min-h-screen bg-white text-crusta-950">
        {!isEmbed ? <NavBar /> : null}
        <div className={`${!isEmbed ? 'md:pl-60 md:pt-[84px]' : ''} site-content bg-white`}>
          <RouteErrorBoundary>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/browse" element={<Browse />} />
                <Route path="/personality-records" element={<People />} />
                <Route path="/creator-record" element={<CreatorRecord />} />
                <Route path="/creators" element={<Navigate to="/personality-records" replace />} />
                <Route path="/creators/top250" element={<TopCreators />} />
                <Route path="/creators/rising" element={<RisingCreators />} />
                <Route path="/creators/new" element={<NewCreators />} />
                <Route path="/creators/:slug" element={<CreatorProfile />} />
                <Route path="/episodes" element={<EpisodesIndex />} />
                <Route path="/episodes/top" element={<TopEpisodes />} />
                <Route path="/episodes/topics" element={<EpisodeTopics />} />
                <Route path="/topics/:tag" element={<TopicPage />} />
                <Route path="/discover" element={<Discover />} />
                <Route path="/discover/hidden-gems" element={<HiddenGems />} />
                <Route path="/discover/picks" element={<EditorialPicks />} />
                <Route path="/discover/rabbit-holes" element={<RabbitHoles />} />
                <Route path="/discover/timestamps" element={<TimestampSearch />} />
                <Route path="/discover/cold-start" element={<ColdStartQuiz />} />
                <Route path="/discover/listening-rooms" element={<ListeningRooms />} />
                <Route path="/playlists" element={<CommunityPlaylists />} />
                <Route path="/playlists/new" element={<NewPlaylist />} />
                <Route path="/playlists/:slug" element={<PlaylistDetail />} />
                <Route path="/embed/playlists/:token" element={<PlaylistEmbed />} />
                <Route path="/charts" element={<Charts />} />
                <Route path="/charts/reviews" element={<Reviews />} />
                <Route path="/charts/hall-of-fame" element={<HallOfFame />} />
                <Route path="/guests" element={<GuestsIndex />} />
                <Route path="/guests/marketplace" element={<GuestMarketplace />} />
                <Route path="/guests/:name" element={<GuestPage />} />
                <Route path="/community/leaderboard" element={<CommunityLeaderboard />} />
                <Route path="/community/polls" element={<CommunityPolls />} />
                <Route path="/community/contribute" element={<CommunityContribute />} />
                <Route path="/shows/:id" element={<ShowPage />} />
                <Route path="/episodes/:id" element={<EpisodePage />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-record"
                  element={
                    <ProtectedRoute>
                      <MyRecord />
                    </ProtectedRoute>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/profile/:username" element={<Profile />} />
                <Route path="/journalist/:slug" element={<JournalistProfile />} />
                <Route path="/embed/journalist/:slug" element={<JournalistProfile />} />
                <Route
                  path="/edit-profile"
                  element={
                    <ProtectedRoute>
                      <EditProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route path="/podcasts" element={<Navigate to="/browse?tab=podcasts" replace />} />
                <Route path="/podcasts/hosts" element={<PodcastHosts />} />
                <Route path="/podcasts/hosts/:host" element={<PodcastHostDetail />} />
                <Route path="/podcasts/creators" element={<PodcastCreators />} />
                <Route path="/podcasts/people" element={<PodcastPeopleList />} />
                <Route path="/podcasts/people/:name" element={<PodcastPersonDetail />} />
<Route path="/podcasts/:id" element={<PodcastDetail />} />
                 <Route path="/debug/podcast-system" element={<PodcastDiagnostics />} />
                 <Route path="/knowledge" element={<Knowledge />} />
                <Route path="/knowledge/:id" element={<KnowledgeEntryPage />} />
                <Route path="/summaries" element={<Navigate to="/" replace />} />
                <Route path="/summaries/browse" element={<SummariesBrowse />} />
                <Route path="/summaries/new" element={<SummariesEditor />} />
                <Route path="/summaries/topics/:topic" element={<SummaryTopics />} />
                <Route path="/summaries/lists" element={<SummaryListsPage />} />
                <Route path="/summaries/lists/:listId" element={<SummaryListsPage />} />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <NotificationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/page/:slug" element={<PageView />} />
                <Route
                  path="/reading-list"
                  element={
                    <ProtectedRoute>
                      <ReadingListPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/summaries/:id" element={<EpisodeSummaryPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </RouteErrorBoundary>
          {!isEmbed ? <ReviewedByBenPopup /> : null}
          {!isEmbed ? <AdvertisingRevenuePopup /> : null}
          <Toaster />
        </div>
      </div>
    </>
  )
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <RouteErrorBoundary>
          <SitePasswordGate>
            <AppShell />
          </SitePasswordGate>
        </RouteErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  )
}

export default App
